import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import Sidebar from "./Sidebar";
import { Topbar } from "./Topbar";
import { Toaster } from "./ui/sonner";
import { MobileBottomNav } from "./ui/mobile-bottom-nav";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import {
    FileText, ShoppingBag, Wrench, Package, CheckSquare, AlertTriangle,
    Wallet, Receipt, FileSpreadsheet, Settings, Sparkles, Database,
    ShieldCheck, UserCog,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export const AppLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [transaksiOpen, setTransaksiOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();
    const { user } = useAuth();

    const go = (path, closers = []) => {
        closers.forEach((fn) => fn(false));
        nav(path);
    };

    const transaksiItems = [
        { path: "/spk", label: "SPK / Job Order", icon: FileText },
        { path: "/bon-kain", label: "Bon Kain", icon: ShoppingBag },
        { path: "/bon-peralatan", label: "Bon Peralatan", icon: Wrench },
        { path: "/setor-barang", label: "Setor Barang Jadi", icon: Package },
        { path: "/qc", label: "Pemeriksaan QC", icon: CheckSquare },
        { path: "/reject-perbaikan", label: "Reject Perbaikan", icon: AlertTriangle },
        { path: "/kasbon", label: "Kasbon", icon: Wallet },
        { path: "/invoice", label: "Invoice", icon: Receipt },
        { path: "/laporan", label: "Laporan / Export", icon: FileSpreadsheet },
    ];

    const menuItems = [
        { path: "/vendor", label: "Vendor", icon: Package },
        { path: "/produk", label: "Produk & Harga", icon: Package },
        { path: "/jenis-kain", label: "Kain", icon: ShoppingBag },
        { path: "/peralatan", label: "Bahan Penolong", icon: Wrench },
        { path: "/pengaturan", label: "Profil Perusahaan", icon: Settings },
        { path: "/konfigurasi-ai", label: "Konfigurasi AI", icon: Sparkles },
        { path: "/backup-data", label: "Backup Data", icon: Database },
    ];
    if (user?.is_owner) {
        menuItems.push({ path: "/account-center", label: "Account Center", icon: ShieldCheck });
        menuItems.push({ path: "/users", label: "Petugas / Login (legacy)", icon: UserCog });
    }

    const rowCls = (active) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
            active
                ? "bg-[var(--apple-blue-soft)] text-[var(--apple-blue)] font-medium"
                : "text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
        }`;

    return (
        <div className="app-shell-outer">
            <div className="app-shell-inner">
                <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="flex-1 min-w-0 flex flex-col">
                    <Topbar onOpenMobileMenu={() => setSidebarOpen(true)} />
                    <main className="flex-1 px-4 md:px-6 lg:px-8 py-4 md:py-6 main-mobile-padbottom overflow-x-hidden">
                        <Outlet />
                    </main>
                </div>
            </div>

            {/* Mobile bottom nav */}
            <MobileBottomNav
                onOpenTransaksi={() => setTransaksiOpen(true)}
                onOpenMenu={() => setMenuOpen(true)}
            />

            {/* Transaksi Sheet (mobile) */}
            <Sheet open={transaksiOpen} onOpenChange={setTransaksiOpen}>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-0" data-testid="mobile-transaksi-sheet">
                    <div className="sticky top-0 bg-white border-b" style={{ borderColor: "var(--border)" }}>
                        <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mt-2 mb-3" />
                        <SheetHeader className="px-4 pb-3">
                            <SheetTitle className="text-left text-base">Transaksi</SheetTitle>
                        </SheetHeader>
                    </div>
                    <div className="p-3 space-y-1">
                        {transaksiItems.map((it) => {
                            const Ic = it.icon;
                            const active = loc.pathname.startsWith(it.path);
                            return (
                                <button
                                    key={it.path}
                                    onClick={() => go(it.path, [setTransaksiOpen])}
                                    className={rowCls(active)}
                                    data-testid={`transaksi-sheet-${it.path.replace(/\//g, "-")}`}
                                >
                                    <Ic className="w-5 h-5 flex-shrink-0" />
                                    <span>{it.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Menu Sheet (mobile) */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-0" data-testid="mobile-menu-sheet">
                    <div className="sticky top-0 bg-white border-b" style={{ borderColor: "var(--border)" }}>
                        <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mt-2 mb-3" />
                        <SheetHeader className="px-4 pb-3">
                            <SheetTitle className="text-left text-base">Menu</SheetTitle>
                        </SheetHeader>
                    </div>
                    <div className="p-3 space-y-1">
                        {menuItems.map((it) => {
                            const Ic = it.icon;
                            const active = loc.pathname.startsWith(it.path);
                            return (
                                <button
                                    key={it.path}
                                    onClick={() => go(it.path, [setMenuOpen])}
                                    className={rowCls(active)}
                                    data-testid={`menu-sheet-${it.path.replace(/\//g, "-")}`}
                                >
                                    <Ic className="w-5 h-5 flex-shrink-0" />
                                    <span>{it.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </SheetContent>
            </Sheet>

            <Toaster richColors position="top-right" />
        </div>
    );
};
